import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Home, Menu } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { Card, CardContent } from '../../../../components/ui/Card';

const MAX_ZS_MENU_ITEMS = [
  {
    id: 'zs-common-devices',
    title: 'Zs Values : Common Devices',
    subtitle: null,
    icon: 'chevron',
  },
  {
    id: 'zs-mccbs',
    title: 'Zs Values : 60947-2 MCCBs',
    subtitle: null,
    icon: 'menu',
  },
  {
    id: 'zs-rcds-tt',
    title: 'Zs Values : RCDs in TT Earthing Systems',
    subtitle: 'Table 41.5',
    icon: 'chevron',
  },
  {
    id: 'ze-rec',
    title: 'Ze Values : REC (DNO / DSO) Ze Values',
    subtitle: null,
    icon: 'chevron',
  },
  {
    id: 'zs-uo-55v-63v',
    title: 'Zs Values : Uo 55v and 63.5v',
    subtitle: 'Table 41.6',
    icon: 'chevron',
  },
  {
    id: 'zs-ze-r1-r2-calc',
    title: 'Zs - Ze = (R1+R2) Calculator',
    subtitle: 'For guidance only',
    icon: 'chevron',
  },
  {
    id: 'max-disconnection-times',
    title: 'Maximum Disconnection Times',
    subtitle: 'Table 41.1',
    icon: 'chevron',
  },
  {
    id: 'mccb-symbols',
    title: 'MCCB Explanation of Settings Symbols',
    subtitle: null,
    icon: 'chevron',
  },
  {
    id: 'max-zs-converter',
    title: 'Max Zs Converter',
    subtitle: 'Convert old Zs values to 18th edition Zs values',
    icon: 'chevron',
  },
];

function MenuItemCard({ item, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick?.(item)}
      className="w-full text-left active:opacity-90 transition-opacity"
    >
      <Card className="min-h-[60px] flex items-center active:opacity-90 transition-opacity bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <CardContent className="flex items-center justify-between gap-4 p-4 w-full">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
              {item.title}
            </h3>
            {item.subtitle && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                {item.subtitle}
              </p>
            )}
          </div>
          <div className="flex-shrink-0 text-gray-500 dark:text-gray-400">
            {item.icon === 'menu' ? (
              <Menu className="h-5 w-5" aria-hidden />
            ) : (
              <ChevronRight className="h-5 w-5" aria-hidden />
            )}
          </div>
        </CardContent>
      </Card>
    </button>
  );
}

export default function MaxZsValues() {
  const location = useLocation();
  const isMobile = location.pathname.startsWith('/tenant/mobile');
  const basePath = isMobile ? '/tenant/mobile/calculators' : '/tenant/tools/calculators';
  const backPath = `${basePath}`;
  const homePath = isMobile ? '/tenant/mobile' : '/tenant/dashboard';

  const navigate = useNavigate();
  const maxZsBasePath = isMobile ? '/tenant/mobile/calculators' : '/tenant/tools/calculators';

  const handleItemClick = (item) => {
    const routes = {
      'zs-common-devices': `${maxZsBasePath}/max-zs-values/zs-common-devices`,
    };
    const path = routes[item.id];
    if (path) navigate(path);
  };

  const content = (
    <div className="max-w-2xl mx-auto">
      {/* Header bar - green */}
      <div className="flex items-center justify-between bg-green-600 dark:bg-green-700 px-4 py-3 rounded-t-lg -mx-4 -mt-4 mb-4 sm:rounded-t-none sm:mx-0 sm:mt-0 sm:-mx-0 sm:-mt-0 sm:rounded-lg">
        <Link
          to={backPath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-lg font-semibold text-white">Max Zs Values</h1>
        <Link
          to={homePath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Home"
        >
          <Home className="h-5 w-5" />
        </Link>
      </div>

      {/* Menu items */}
      <div className={`space-y-3 ${isMobile ? 'pb-8' : ''}`}>
        {MAX_ZS_MENU_ITEMS.map((item) => (
          <MenuItemCard key={item.id} item={item} onClick={handleItemClick} />
        ))}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="px-4 py-6 pb-8 min-h-screen bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        {content}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Max Zs Values
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Calculate maximum Zs values and related tables
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {content}
        </div>
      </div>
    </Layout>
  );
}
